import { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CircleNotch } from "@phosphor-icons/react";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import JobsPage from "@/pages/JobsPage";
import InspectionPage from "@/pages/InspectionPage";
import TasksPage from "@/pages/TasksPage";
import InventoryPage from "@/pages/InventoryPage";
import MaintenancePage from "@/pages/MaintenancePage";
import AdminPage from "@/pages/AdminPage";
import CustomerPortalPage from "@/pages/CustomerPortalPage";
import MaterialListPage from "@/pages/MaterialListPage";
import VoltageDetectorPage from "@/pages/VoltageDetectorPage";
import CalculatorsPage from "@/pages/CalculatorsPage";
import NewsPage from "@/pages/NewsPage";
import SponsoredPage from "@/pages/SponsoredPage";
import CameraFeedPage from "@/pages/CameraFeedPage";
import SafetyPage from "@/pages/SafetyPage";
import ToolTrackerPage from "@/pages/ToolTrackerPage";
import PermitsPage from "@/pages/PermitsPage";
import PlansTakeoffPage from "@/pages/PlansTakeoffPage";
import BottomNav from "@/components/BottomNav";
import VoiceFAB from "@/components/VoiceFAB";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [voiceOpen, setVoiceOpen] = useState(false);

  const currentTab = (() => {
    const p = location.pathname;
    if (p === "/" || p === "/dashboard") return "dashboard";
    if (p.startsWith("/jobs")) return "jobs";
    if (p.startsWith("/voltage")) return "voltage";
    if (p.startsWith("/materials")) return "materials";
    if (p.startsWith("/inspect")) return "inspect";
    if (p.startsWith("/calc")) return "calc";
    if (p.startsWith("/permits")) return "permits";
    if (p.startsWith("/plans")) return "plans";
    if (p.startsWith("/safety")) return "safety";
    if (p.startsWith("/tool-tracker")) return "tooltracker";
    if (p.startsWith("/camera")) return "camera";
    if (p.startsWith("/news")) return "news";
    if (p.startsWith("/sponsored")) return "sponsored";
    if (p.startsWith("/tasks")) return "tasks";
    if (p.startsWith("/inventory")) return "inventory";
    if (p.startsWith("/maintenance")) return "maintenance";
    if (p.startsWith("/admin")) return "admin";
    if (p.startsWith("/customer")) return "customers";
    return "dashboard";
  })();

  const handleNav = useCallback((tab) => {
    const routes = {
      dashboard: "/", jobs: "/jobs", inspect: "/inspect", materials: "/materials",
      voltage: "/voltage", calc: "/calc", news: "/news", sponsored: "/sponsored",
      camera: "/camera", safety: "/safety", tooltracker: "/tool-tracker",
      tasks: "/tasks", inventory: "/inventory", maintenance: "/maintenance",
      admin: "/admin", customers: "/customers"
    };
    navigate(routes[tab] || "/");
  }, [navigate]);

  // If on login page, just show login
  if (location.pathname === "/login") {
    return (
      <div className="min-h-screen bg-white">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        </Routes>
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar with user info */}
      {user && (
        <div className="fixed top-0 left-0 right-0 bg-white border-b border-[#E5E5E5] z-[10000] px-4 py-1.5 flex items-center justify-between">
          <span className="text-[10px] font-mono-data text-[#404040]">
            {user.name || user.email} <span className="text-[#002FA7]">({user.role?.toUpperCase()})</span>
          </span>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="text-[10px] font-mono-data text-[#FF5A00] hover:underline"
          >
            LOGOUT
          </button>
        </div>
      )}
      <div className={`pb-nav ${user ? "pt-8" : ""}`}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* All authenticated users */}
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/news" element={<ProtectedRoute><NewsPage /></ProtectedRoute>} />
          <Route path="/sponsored" element={<ProtectedRoute><SponsoredPage /></ProtectedRoute>} />
          <Route path="/calc" element={<ProtectedRoute><CalculatorsPage /></ProtectedRoute>} />

          {/* Worker + Admin routes */}
          <Route path="/jobs" element={<ProtectedRoute allowedRoles={["admin","worker"]}><JobsPage /></ProtectedRoute>} />
          <Route path="/inspect" element={<ProtectedRoute allowedRoles={["admin","worker"]}><InspectionPage /></ProtectedRoute>} />
          <Route path="/materials" element={<ProtectedRoute allowedRoles={["admin","worker"]}><MaterialListPage /></ProtectedRoute>} />
          <Route path="/voltage" element={<ProtectedRoute allowedRoles={["admin","worker"]}><VoltageDetectorPage /></ProtectedRoute>} />
          <Route path="/camera" element={<ProtectedRoute allowedRoles={["admin","worker"]}><CameraFeedPage /></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute allowedRoles={["admin","worker"]}><TasksPage /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute allowedRoles={["admin","worker"]}><InventoryPage /></ProtectedRoute>} />
          <Route path="/maintenance" element={<ProtectedRoute allowedRoles={["admin","worker"]}><MaintenancePage /></ProtectedRoute>} />
          <Route path="/tool-tracker" element={<ProtectedRoute allowedRoles={["admin","worker"]}><ToolTrackerPage /></ProtectedRoute>} />
          <Route path="/permits" element={<ProtectedRoute allowedRoles={["admin","worker"]}><PermitsPage /></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute allowedRoles={["admin","worker"]}><PlansTakeoffPage /></ProtectedRoute>} />

          {/* Admin only */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPage /></ProtectedRoute>} />
          <Route path="/safety" element={<ProtectedRoute allowedRoles={["admin"]}><SafetyPage /></ProtectedRoute>} />

          {/* Admin + Customer */}
          <Route path="/customers" element={<ProtectedRoute allowedRoles={["admin","customer"]}><CustomerPortalPage /></ProtectedRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {user && <VoiceFAB open={voiceOpen} onToggle={() => setVoiceOpen(!voiceOpen)} />}
      {user && <BottomNav active={currentTab} onNavigate={handleNav} />}
      <Toaster position="top-center" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
